// R58 — THE BUTTON GRID EVERY CONFIG-DRIVEN ROOM IS BUILT OUT OF, DECLARED ONCE.
//
// Five menus (`src/lib/reading-menus.ts`), five grids, and the five were typed out separately in
// App.tsx — the same `<section className="mini-reports">`, the same `section__header--narrow`, the
// same `.mini-reports__grid`, the same button, five times, differing only in a class, an aria label,
// a heading and what a tap opens. That is R41's currency: a DERIVATION, not an extraction. Two
// things it buys beyond the lines.
//
//   1. **THE `configs.length ? … : null` LAW IS ONE DECISION NOW.** It is what makes a failed read a
//      band of the room that is not there rather than an empty grid, which is the whole reason R58
//      exists — and it was written five times, once per surface, where nothing could run any of it.
//   2. **A GATE CALLS IT.** Props in, tree out, no hook, so `scripts/r58-reading-menus.mjs` renders
//      every grid this house draws and reads the tree back: the anchor class the four browser checks
//      point at, the per-row key, and the two-subject tag that appears on exactly one of the five.
//
// The differences between the five are PROPS rather than five copies: the mini row has no heading,
// the numerology row has a lead line and prints each row's blurb, and only the deep readings carry
// the two-people tag. A prop nobody passes draws nothing at all, so a room gets the house's grid by
// rendering it.

// The least a row must carry to be drawn. Every menu's own type is wider than this and passes.
export type ReadingMenuRow = {
  key: string;
  label: string;
  blurb?: string;
  twoSubject?: boolean;
};

export function ReadingMenuSection<T extends ReadingMenuRow>({
  rows,
  ariaLabel,
  className = "",
  title,
  lead,
  tag,
  showBlurb = false,
  onPick,
}: {
  rows: T[];
  ariaLabel: string;
  // The extra class the room's own stylesheet keys off, beside the shared `mini-reports`.
  className?: string;
  // Absent on the quick-readings row, which sits directly under the section above it and heads
  // itself with nothing — that is a design decision, so it is expressed by NOT passing a heading.
  title?: string;
  lead?: string;
  // Only the deep readings have a two-subject row, and the words for it are the caller's (this file
  // imports nothing from the catalog).
  tag?: string;
  showBlurb?: boolean;
  onPick: (row: T) => void;
}) {
  // **A MENU WITH NO ROWS DRAWS NOTHING**, which is the law R58 is about: an empty answer is a real
  // answer (the catalog has no rows) and a FAILED read never reaches here at all, because the room
  // keeps its previous rows and says so in a sentence instead.
  if (!rows || !rows.length) return null;
  return (
    <section className={`mini-reports${className ? ` ${className}` : ""}`} aria-label={ariaLabel}>
      {title ? <div className="section__header section__header--narrow"><h2>{title}</h2></div> : null}
      {lead ? <p className="numerology-cats__lead">{lead}</p> : null}
      <div className="mini-reports__grid">
        {rows.map((row) => (
          <button key={row.key} type="button" className="mini-report-btn" onClick={() => onPick(row)}>
            <span className="mini-report-btn__label">{row.label}</span>
            {showBlurb && row.blurb ? <span className="mini-report-btn__blurb">{row.blurb}</span> : null}
            {row.twoSubject && tag ? <span className="mini-report-btn__tag">{tag}</span> : null}
          </button>
        ))}
      </div>
    </section>
  );
}
